import { ChangeEvent, useState } from "react";
import cn from "classnames";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { ColorPicker } from "components/UI/ColorPicker";
import { CustomSelect } from "components/UI/CustomSelect";
import { Cog, Trash } from "components/UI/Icons";
import { Info, INFO_VARIANT } from "components/UI/Info";
import { Input, INPUT_VARIANT } from "components/UI/Input";
import { PACKAGE_OPTIONS, PackageOptionType } from "constants/packageOptions.constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { CargoRow as CargoRowType, InputNumber } from "models/packingList";
import {
  getInvalidRequiredCargoFields,
  isFilledRequiredNumber,
  removeCargoRow,
  selectCargoRowById,
  selectOptions,
  selectPalletFitErrorByCargoId,
  selectShowCargoValidation,
  updateCargoRow,
} from "store";
import { parseInputNumber } from "utils/parseInputNumber.utils";
import { fromDisplayLength, fromDisplayWeight, toDisplayLength, toDisplayWeight } from "utils/units.utils";
import { CargoRowProps } from "./CargoTable.types";
import { PackageSettingsModal } from "./PackageSettings";
import s from "./CargoTable.module.scss";

type UnitField = keyof Pick<CargoRowType, "length" | "width" | "height" | "weight">;

const EMPTY_PLACEHOLDER = "0";

const toVisibleNumber = (value: InputNumber): string | number => (isFilledRequiredNumber(value) ? value! : "");

export const CargoRow = ({ rowId, groupId }: CargoRowProps) => {
  const dispatch = useAppDispatch();
  const row = useAppSelector(selectCargoRowById(groupId, rowId));
  const { lengthUnits, weightUnits } = useAppSelector(selectOptions);
  const showValidation = useAppSelector(selectShowCargoValidation);
  const palletFitError = useAppSelector(selectPalletFitErrorByCargoId(rowId));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  if (!row) return null;

  const packageType = PACKAGE_OPTIONS.find((option) => option.value === row.packageType) ?? PACKAGE_OPTIONS[0];
  const invalidFields = showValidation ? new Set(getInvalidRequiredCargoFields(row)) : null;
  const fieldError = (field: UnitField | "qty") => Boolean(invalidFields?.has(field));

  const handleChange = (field: UnitField) => (e: ChangeEvent<HTMLInputElement>) => {
    const displayValue = parseInputNumber(e.target.value);
    const value =
      field === "weight" ? fromDisplayWeight(displayValue, weightUnits) : fromDisplayLength(displayValue, lengthUnits);
    dispatch(updateCargoRow({ groupId, rowId, [field]: value }));
  };

  const handleQtyChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(updateCargoRow({ groupId, rowId, qty: parseInputNumber(e.target.value) }));
  };

  const numberInputClass = (field: UnitField | "qty") => cn(s.numberInput, fieldError(field) && s.placeholderError);
  const productName = palletFitError?.data?.attributes?.name || row.product || "Product";
  const palletErrorMessage = palletFitError?.message || "Unable to install on pallet";

  return (
    <>
      <div className={cn(s.rowWrap, { [s.rowWrapError]: Boolean(palletFitError) })}>
        <div className={cn(s.row, { [s.rowError]: Boolean(palletFitError) })}>
          <div className={s.cell}>
            <Input
              type="text"
              variant={INPUT_VARIANT.GHOST}
              value={row.product}
              onChange={(e) => dispatch(updateCargoRow({ groupId, rowId, product: e.target.value }))}
            />
          </div>
          <div className={s.cell}>
            <CustomSelect<PackageOptionType>
              className={s.select}
              componentsClassNames={{ menu: s.menu, menuPortal: s.menuPortal }}
              options={PACKAGE_OPTIONS}
              value={packageType}
              handleChange={(option) =>
                dispatch(
                  updateCargoRow({
                    groupId,
                    rowId,
                    packageType: option.value,
                  })
                )
              }
            />
          </div>
          <div className={s.cell}>
            <Input
              type="number"
              variant={INPUT_VARIANT.GHOST}
              inputClassName={numberInputClass("length")}
              placeholder={EMPTY_PLACEHOLDER}
              value={toVisibleNumber(toDisplayLength(row.length, lengthUnits))}
              onChange={handleChange("length")}
            />
          </div>
          <div className={s.cell}>
            <Input
              type="number"
              variant={INPUT_VARIANT.GHOST}
              inputClassName={numberInputClass("width")}
              placeholder={EMPTY_PLACEHOLDER}
              value={toVisibleNumber(toDisplayLength(row.width, lengthUnits))}
              onChange={handleChange("width")}
            />
          </div>
          <div className={s.cell}>
            <Input
              type="number"
              variant={INPUT_VARIANT.GHOST}
              inputClassName={numberInputClass("height")}
              placeholder={EMPTY_PLACEHOLDER}
              value={toVisibleNumber(toDisplayLength(row.height, lengthUnits))}
              onChange={handleChange("height")}
            />
          </div>
          <div className={s.cell}>
            <Input
              type="number"
              variant={INPUT_VARIANT.GHOST}
              inputClassName={numberInputClass("weight")}
              placeholder={EMPTY_PLACEHOLDER}
              value={toVisibleNumber(toDisplayWeight(row.weight, weightUnits))}
              onChange={handleChange("weight")}
            />
          </div>
          <div className={s.cell}>
            <Input
              type="number"
              variant={INPUT_VARIANT.GHOST}
              inputClassName={numberInputClass("qty")}
              placeholder={EMPTY_PLACEHOLDER}
              value={toVisibleNumber(row.qty)}
              onChange={handleQtyChange}
            />
          </div>
          <div className={cn(s.cell, s.tdColor)}>
            <ColorPicker value={row.color} onChange={(color) => dispatch(updateCargoRow({ groupId, rowId, color }))} />
          </div>
          <div className={cn(s.cell, s.tdActions)}>
            <div className={s.actionsBox}>
              <Button
                className={s.actionBtn}
                variant={BUTTON_VARIANT.ONLY_ICON_TRANSPARENT}
                onClick={() => setIsSettingsOpen(true)}
                aria-label="Package settings"
              >
                <Cog />
              </Button>
              <Button
                className={s.actionBtn}
                variant={BUTTON_VARIANT.ONLY_ICON_TRANSPARENT}
                onClick={() => dispatch(removeCargoRow({ groupId, rowId }))}
                aria-label="Delete cargo"
              >
                <Trash />
              </Button>
            </div>
          </div>
        </div>

        {palletFitError ? (
          <div className={s.palletError}>
            <Info variant={INFO_VARIANT.ERROR} className={s.palletErrorInfo}>
              <strong>{productName}</strong>
              {`: ${palletErrorMessage}. This product does not fit the selected pallet dimensions.`}
            </Info>
          </div>
        ) : null}
      </div>

      <PackageSettingsModal
        groupId={groupId}
        rowId={rowId}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};
